import React from 'react';
import { REFERRAL_STAGE_LABELS, type ReferralStage } from '@valutten/shared-schemas/dist/partnerNetwork';
import type { PartnerReferralView, ReferralPartner } from '../../hooks/usePartnerReferrals';
import { formatCentsAud, formatReferralDate } from '../../utils/partnerReferrals';

const STAGE_PILL: Record<ReferralStage, string> = {
  submitted: 'bg-gray-600/30 text-gray-300 border-gray-500/40',
  contacted: 'bg-gray-600/30 text-gray-200 border-gray-500/40',
  strategy_call: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  engaged: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  searching: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  under_contract: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  settled: 'bg-green-500/15 text-green-300 border-green-500/40',
  lapsed: 'bg-red-500/10 text-red-300 border-red-500/30',
};

export const StagePill: React.FC<{ stage: ReferralStage }> = ({ stage }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${
      STAGE_PILL[stage] ?? STAGE_PILL.submitted
    }`}
  >
    {REFERRAL_STAGE_LABELS[stage] ?? stage}
  </span>
);

/** The fee is priced once the purchase price is known; a lapsed referral with no price owes nothing. */
function feeText(r: PartnerReferralView): string {
  if (typeof r.referralFeeIncGstCents === 'number') return formatCentsAud(r.referralFeeIncGstCents);
  return r.stage === 'lapsed' ? '—' : 'Priced at contract';
}

interface ReferralsTableProps {
  referrals: PartnerReferralView[];
  partners: ReferralPartner[];
  /**
   * useAuth().hideCommissions. The server nulls fee fields for these users, so
   * the fee column is omitted entirely rather than reading "Priced at contract".
   */
  hideFees?: boolean;
  /** Opens a referral's detail page. Omitted = rows are not interactive. */
  onSelect?: (referralId: string) => void;
}

export const ReferralsTable: React.FC<ReferralsTableProps> = ({ referrals, partners, hideFees = false, onSelect }) => {
  const partnerName = (id: string) => partners.find((p) => p.partnerId === id)?.name ?? id;
  // A broker sees only their own referrals, so the column is shown only when
  // the list holds more than one referring broker (an owner or administrator).
  const brokerNames = new Set(referrals.map((r) => r.referringBrokerName).filter(Boolean));
  const showBroker = brokerNames.size > 1;

  return (
    <>
      {/* Phone: stacked cards */}
      <ul className="space-y-3 md:hidden">
        {referrals.map((r) => (
          <li key={r.id} className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(r.id)}
                    className="truncate text-left text-sm font-medium text-blue-300 hover:text-blue-200"
                  >
                    {r.clientName}
                  </button>
                ) : (
                  <p className="truncate text-sm font-medium text-gray-100">{r.clientName}</p>
                )}
                <p className="truncate text-xs text-gray-400">
                  {partnerName(r.partnerId)} · {formatReferralDate(r.createdAt)}
                </p>
                {showBroker && r.referringBrokerName && (
                  <p className="truncate text-xs text-gray-400">Broker: {r.referringBrokerName}</p>
                )}
              </div>
              <StagePill stage={r.stage} />
            </div>
            {!hideFees && (
              <p className="mt-2 text-xs text-gray-400">
                Fee on settlement: <span className="text-gray-200">{feeText(r)}</span>
              </p>
            )}
          </li>
        ))}
      </ul>

      {/* Tablet and up: table */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-400">
            <tr>
              <th className="px-4 py-3 font-medium">Client</th>
              {showBroker && <th className="px-4 py-3 font-medium">Broker</th>}
              <th className="px-4 py-3 font-medium">Partner</th>
              <th className="px-4 py-3 font-medium">Submitted</th>
              <th className="px-4 py-3 font-medium">Stage</th>
              {!hideFees && <th className="px-4 py-3 font-medium text-right">Fee on settlement (inc GST)</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/70">
            {referrals.map((r) => (
              <tr key={r.id} className="hover:bg-gray-800/40">
                <td className="px-4 py-3 font-medium text-gray-100">
                  {onSelect ? (
                    <button
                      type="button"
                      onClick={() => onSelect(r.id)}
                      className="text-left text-blue-300 hover:text-blue-200 hover:underline"
                    >
                      {r.clientName}
                    </button>
                  ) : (
                    r.clientName
                  )}
                </td>
                {showBroker && <td className="px-4 py-3 text-gray-300">{r.referringBrokerName ?? '—'}</td>}
                <td className="px-4 py-3 text-gray-300">{partnerName(r.partnerId)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-300">{formatReferralDate(r.createdAt)}</td>
                <td className="px-4 py-3"><StagePill stage={r.stage} /></td>
                {!hideFees && (
                  <td
                    className={`px-4 py-3 text-right whitespace-nowrap ${
                      typeof r.referralFeeIncGstCents === 'number' ? 'text-gray-100' : 'text-gray-400'
                    }`}
                  >
                    {feeText(r)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ReferralsTable;
